import { Box } from "@mui/material";
import { fabric } from "fabric";
import { useCallback, useEffect, useRef } from "react";
import { canvasDataToCanvasJSON } from "./lib/canvasDataToCanvasJson";
import { CanvasData } from "./type";

const CANVAS_ID = "canvas";

export const CanvasViewer = ({
  imageUrl,
  maxWidth,
  canvasData,
}: {
  imageUrl: string;
  maxWidth?: number;
  canvasData: CanvasData[];
}) => {
  const canvasRef = useRef<fabric.Canvas | undefined>(undefined);
  const canvasLapper = useRef<HTMLDivElement>(null);
  const isCanvasExistRef = useRef(false);
  const beforeImageUrlRef = useRef("");
  const initialWidthRef = useRef(300);

  const setZoomWidthHeight = (canvasWidth: number, canvasHeight: number) => {
    if (canvasRef.current === undefined) {
      return;
    }
    canvasRef.current?.setZoom(canvasWidth / initialWidthRef.current);
    canvasRef.current?.setWidth(canvasWidth);
    canvasRef.current?.setHeight(canvasHeight);
  };

  const loadCanvasFromCanvasData = () => {
    if (canvasRef.current === undefined) {
      return;
    }
    const _JSON = canvasDataToCanvasJSON(canvasData, canvasRef);
    const canvasWidth = canvasRef.current.width as number;
    const canvasHeight = canvasRef.current.height as number;
    canvasRef.current.loadFromJSON(_JSON, () => {
      setZoomWidthHeight(canvasWidth, canvasHeight);
    });
  };

  useEffect(() => {
    loadCanvasFromCanvasData();
  }, [canvasData]);

  const loadBackgroundImage = useCallback(
    (canvasWidth: number) => {
      fabric.Image.fromURL(imageUrl, (image) => {
        const imageWidth = image.width;
        const imageHeight = image.height;
        if (!imageWidth || !imageHeight) {
          return;
        }
        const canvasHeight = (canvasWidth / imageWidth) * imageHeight;
        setZoomWidthHeight(canvasWidth, canvasHeight);
      });
    },
    [imageUrl]
  );
  useEffect(() => {
    if (canvasRef.current === undefined) {
      return;
    }
    if (beforeImageUrlRef.current !== imageUrl) {
      beforeImageUrlRef.current = imageUrl;
      canvasRef.current.clear();
      const canvasWidth = canvasRef.current!.width as number;
      loadBackgroundImage(canvasWidth);
    }
  }, [imageUrl]);

  useEffect(() => {
    if (
      canvasLapper.current &&
      (imageUrl ?? "") !== "" &&
      !isCanvasExistRef.current
    ) {
      isCanvasExistRef.current = true;
      beforeImageUrlRef.current = imageUrl;
      const canvasWidth = canvasLapper.current!.offsetWidth;

      fabric.Image.fromURL(imageUrl, (image) => {
        const imageWidth = image.width;
        const imageHeight = image.height;
        if (!imageWidth || !imageHeight) {
          return;
        }
        const canvasHeight = (canvasWidth / imageWidth) * imageHeight;
        canvasRef.current = new fabric.Canvas(CANVAS_ID, {
          isDrawingMode: false, // 手書きモード
          allowTouchScrolling: true, //スマホで拡大出来るようにする
          width: canvasWidth,
          height: canvasHeight,
          backgroundColor: "rgba(0,0,0,0)",
          //backgroundImage: image,
        });
        initialWidthRef.current = canvasWidth;
        canvasRef.current.freeDrawingBrush.width = 5;
        canvasRef.current.loadFromJSON(
          canvasDataToCanvasJSON(canvasData, canvasRef),
          () => {}
        );
      });
    }
  }, []);

  const onResize = useCallback(() => {
    if (canvasLapper.current && imageUrl) {
      const canvasWidth = canvasLapper.current.offsetWidth;
      fabric.Image.fromURL(imageUrl, (image) => {
        const imageWidth = image.width;
        const imageHeight = image.height;
        if (!imageWidth || !imageHeight) {
          return;
        }
        const canvasHeight = (canvasWidth / imageWidth) * imageHeight;
        canvasRef.current?.setZoom(canvasWidth / initialWidthRef.current);
        canvasRef.current?.setWidth(canvasWidth);
        canvasRef.current?.setHeight(canvasHeight);
        /*canvasRef.current?.setBackgroundImage(image, () => {
          image.scaleToWidth(canvasWidth);
          makeBackgroundImageUnerasable(canvasRef);
        });*/
      });
    }
  }, [imageUrl]);

  window.onresize = onResize;

  return (
    <Box>
      <div
        ref={canvasLapper}
        id="canvas-lapper"
        style={{
          width: "100%",
          maxWidth,
          margin: "auto",
          position: "relative",
        }}
      >
        <img
          src={imageUrl}
          alt="背景画像"
          style={{
            width: "100%",
            maxWidth,
            position: "absolute",
            top: 0,
            left: 0,
          }}
        />
        <canvas id={CANVAS_ID} />
      </div>
    </Box>
  );
};
